/* section-render.js — instant in-place updates via Shopify's Section Rendering API.
 * Fetches ONLY the requested section HTML (?sections=id) and morphs it into the DOM —
 * used for cart drawer, faceted filters, pagination, predictive search. No full reload. */
window.SectionRender = (() => {
  const cache = new Map();

  // opts.fresh bypasses the memo — REQUIRED for sections whose content
  // changes under an identical URL (the cart drawer: same /cart?sections=
  // key every refresh, so a memo hit would pin the first snapshot forever)
  async function fetchSection(url, sectionId, opts) {
    const fresh = opts && opts.fresh;
    const u = new URL(url, location.origin);
    u.searchParams.set('sections', sectionId);
    const key = u.toString();
    if (!fresh && cache.has(key)) return cache.get(key);
    // fresh renders also carry a unique param: edge caches have served stale
    // (even empty) section payloads for repeated identical URLs
    if (fresh) u.searchParams.set('_ts', String(Date.now()));
    // NO Accept: application/json header — on /cart and /collections/* it
    // triggers Shopify's content negotiation (cart/collection JSON) and the
    // ?sections= param is ignored entirely. The API returns JSON regardless.
    const res = await fetch(u);
    const data = await res.json();
    if (data[sectionId]) cache.set(key, data[sectionId]);
    return data[sectionId];
  }

  // minimal DOM morph: replace innerHTML but keep focus/scroll where possible
  function swap(targetEl, html) {
    const tmp = document.createElement('template');
    tmp.innerHTML = html.trim();
    const fresh = tmp.content.firstElementChild;
    if (!fresh) { targetEl.innerHTML = html; return; }
    targetEl.replaceWith(fresh);
    enhance(fresh); // fresh links must keep swapping in place
    fresh.dispatchEvent(new CustomEvent('section:loaded', { bubbles: true }));
  }

  // intercept faceted-filter / pagination links to swap instead of navigate
  function enhance(root = document) {
    root.querySelectorAll('[data-section-link]').forEach((a) => {
      a.addEventListener('click', async (e) => {
        e.preventDefault();
        const sectionId = a.closest('[data-section-id]')?.dataset.sectionId;
        const target = a.closest('[data-section-id]');
        if (!sectionId || !target) { location.href = a.href; return; }
        target.setAttribute('aria-busy', 'true');
        try {
          const html = await fetchSection(a.href, sectionId);
          swap(target, html);
          history.pushState({}, '', a.href);
        } catch { location.href = a.href; }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => enhance());
  // delegated so it survives section swaps: sort select navigates with sort_by
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-sort-select]');
    if (!sel) return;
    const u = new URL(location.href);
    u.searchParams.set('sort_by', sel.value);
    location.href = u.toString();
  });
  return { fetchSection, swap, enhance };
})();
